import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { Messages } from '../../../codegen/Localize';
import { Projects } from '../../../projects/resources/Projects';
import { CreateScheduleRequest } from '../../../schedule/types/CreateScheduleRequest';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { SpaceInfoItemTypeValue } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IExplorableItemTypeInfo, ISpaceTypeInfo } from '../../../ts/models/resourceInfoParts';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { ContentActionsService } from '../../../commonInfra/contentActions/ContentActions';
import { CreateOrUpdateScheduleActionService } from '../../../ts/services/CreateOrUpdateScheduleAction';
import { CreateSiteAction } from '../../../ts/services/CreateSiteAction';
import { WebHttpClientWithErrorToastInstance } from '../../../httpClient/WebHttpClientWithErrorToast';
import { CreateNewContentInfoRequest, CreateNewFlow, CreateNewLens, CreateNewProject, CreateNewPublishedConnection, CreateNewSchedule, CreateNewSite, CreateNewWorkbook, CreateNewPublishedDataSource } from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import { requestAvailableActionsForEntity } from '../../actions/spaces/AvailableActionsForEntityActions';
import { CreateNewContentInfoRequestAction, CreateNewFlowAction, CreateNewLensAction, CreateNewProjectAction, CreateNewPublishedConnectionAction, CreateNewScheduleAction, CreateNewSiteAction, CreateNewWorkbookAction, CreateNewPublishedDatasourceAction } from '../../actions/spaces/ContentExplorerCreateActions';
import { newWorkbookForDatasource } from '../../actions/spaces/NewWorkbookForDatasourceAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { AdminSelfServiceScheduleDialog, AdminSelfServiceScheduleDialogState } from '../../content/AdminSelfServiceScheduleDialog';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { selectSpaceInfo, selectSpaceInfoItem } from '../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo } from '../../state/SpaceInfo';
import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { createLensDialog } from '../../../lens/LensCreationDialog/LensCreationDialog';
import { SiteUrls } from '@tableau/shared-urls';

export function* handleCreateNewContentInfoRequest(action: CreateNewContentInfoRequestAction) {
  // if app state doesnt have all the data it needs, make those API calls here.
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    yield put(requestAvailableActionsForEntity(spaceInfo.item as IExplorableItemTypeInfo));
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Workbook

const createNewWorbook = (siteUrlName: string, projectId: string) => {
  ContentActionsService.newEmptyWorkbook(siteUrlName, projectId);
};

export function* handleCreateNewWorkbook(action: CreateNewWorkbookAction) {
  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    // If we are on a datasource space, we should create new workbook for the datasource
    const spaceItem: ISpaceTypeInfo = yield select(selectSpaceInfoItem);
    if (spaceItem.contentType === SpaceInfoItemTypeValue.datasource) {
      yield put(newWorkbookForDatasource(spaceItem as IContentInfo));
      return;
    }
    yield call(createNewWorbook, siteUrlName, action.projectId || '');
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Project

const createNewProject = (projectId: string): Promise<IResult> => {
  return ContentActionsService.createProject(projectId);
};

export function* handleCreateNewProject(action: CreateNewProjectAction) {
  try {
    yield call(createNewProject, action.projectId);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function* getProjectLuid(projectId: string) {
  const projectInfo = yield call(Projects.getById.bind(Projects), projectId);
  return projectInfo.luid;
}

// Create new published datasource

const createNewPublishedDatasource = (siteUrlName: string, projectId: string) => {
  // TODO: Do we need to pass the projectId ?
  ContentActionsService.generateTempWorkbookForCreateDatasource(siteUrlName);
};

export function* handleCreateNewPublishedDataSource(action: CreateNewPublishedDatasourceAction) {
  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    yield call(createNewPublishedDatasource, siteUrlName, action.projectId || '');
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create new Published connection
export function* handleCreateNewPublishedConnection(action: CreateNewPublishedConnectionAction) {
  try {
    const contentActionsService = ContentActionsService;
    if (action.projectId) {
      const projectLuid = yield call(getProjectLuid, action.projectId);
      contentActionsService.newPublishedConnection(projectLuid);
    } else {
      contentActionsService.newPublishedConnection();
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Flow
export function* handleCreateNewFlow(action: CreateNewFlowAction) {
  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    ContentActionsService.newFlow(siteUrlName, spaceInfo.item, action.projectId);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Lens
export function* handleCreateNewLens(action: CreateNewLensAction) {
  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const siteUrl = new SiteUrls({ siteUrlName });
    const publishedDatasourceId = action.publishedDatasourceId;
    ReactModalService.openCustom(createLensDialog(siteUrlName, spaceInfo, siteUrl, publishedDatasourceId));
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Site
const createNewSite = (): ng.IPromise<IResult> => {
  const createSiteActionService = ServiceLocator.get<CreateSiteAction>('CreateSiteAction');
  const context = ServiceLocator.getAngularActionContext();
  return createSiteActionService.execute(context.scope);
};

export function* handleCreateNewSite(action: CreateNewSiteAction) {
  try {
    yield call(createNewSite);
    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create new schedule

const createNewSchedule = (): Promise<IResult> => {
  if (FeatureFlags.adminSelfServiceSchedulesEnabled) {
    return AdminSelfServiceScheduleDialog.open((scheduleState: AdminSelfServiceScheduleDialogState) => {
      const request = new CreateScheduleRequest({
        name: scheduleState.name,
        scheduleType: scheduleState.recurrence.recurrenceType,
        scheduleDetails: scheduleState.recurrence.recurrenceDetails,
        scheduledAction: scheduleState.type,
        priority: scheduleState.priority,
        parallel: scheduleState.isParallel,
        enabledForLinkedTasks: scheduleState.enabledForLinkedTasks,
      });

      return new Promise<IResult>((resolve, reject) => {
        WebHttpClientWithErrorToastInstance.sendRequest(request).then(result => {
          CreateOrUpdateScheduleActionService.notifyResult(result, scheduleState.name);
          return resolve(result);
        }).catch(err => reject(err));
      });
    });
  } else {
    return new Promise<IResult>((resolve, reject) => {
      CreateOrUpdateScheduleActionService.createSchedule().then(val => resolve(val)).catch(err => reject(err));
    });
  }
};

export function* handleCreateNewSchedule(action: CreateNewScheduleAction) {
  try {
    yield call(createNewSchedule);
    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootContentCreationSaga(): SagaIterator {
  yield takeLatest(CreateNewContentInfoRequest, handleCreateNewContentInfoRequest);
  yield takeLatest(CreateNewWorkbook, handleCreateNewWorkbook);
  yield takeLatest(CreateNewProject, handleCreateNewProject);
  yield takeLatest(CreateNewPublishedConnection, handleCreateNewPublishedConnection);
  yield takeLatest(CreateNewFlow, handleCreateNewFlow);
  yield takeLatest(CreateNewLens, handleCreateNewLens);
  yield takeLatest(CreateNewSite, handleCreateNewSite);
  yield takeLatest(CreateNewSchedule, handleCreateNewSchedule);
  yield takeLatest(CreateNewPublishedDataSource, handleCreateNewPublishedDataSource);
}
